<div class="bai-res-root" aria-label="Resources tab">

  <!-- ── Loading ──────────────────────────────────────────────────────────── -->
  @if (loading()) {
    <div class="bai-res-loading" aria-busy="true" aria-label="Loading resources">
      @for (_ of [1,2,3,4,5]; track $index) {
        <p-skeleton height="4rem" styleClass="mb-2" />
      }
    </div>
  }

  <!-- ── No session ────────────────────────────────────────────────────────── -->
  @else if (!state()) {
    <div class="bai-res-empty">
      <i class="pi pi-inbox bai-res-empty__icon" aria-hidden="true"></i>
      <p>Select a client to view resources.</p>
    </div>
  }

  <!-- ── Main content ──────────────────────────────────────────────────────── -->
  @else {
    @let s = state()!;
    @let ctx = s.context;
    @let f = s.filters;

    <!-- ── 1. Client Context Snapshot ──────────────────────────────────────── -->
    <section class="bai-res-card bai-res-context" aria-labelledby="ctx-title">
      <h2 id="ctx-title" class="bai-res-section-title">
        <i class="pi pi-user" aria-hidden="true"></i>
        Client Context Snapshot
        <span class="bai-res-context__updated">Updated {{ formatDate(ctx.lastUpdatedAt) }}</span>
      </h2>
      <div class="bai-res-context__grid">
        <div class="bai-res-context__item">
          <span class="bai-res-context__label">Employer</span>
          <span class="bai-res-context__value">{{ ctx.employer }}</span>
        </div>
        <div class="bai-res-context__item">
          <span class="bai-res-context__label">Region</span>
          <span class="bai-res-context__value">{{ ctx.region }}</span>
        </div>
        <div class="bai-res-context__item">
          <span class="bai-res-context__label">Tenure</span>
          <span class="bai-res-context__value">{{ ctx.tenure }}</span>
        </div>
        <div class="bai-res-context__item">
          <span class="bai-res-context__label">Employment Type</span>
          <span class="bai-res-context__value">{{ ctx.employmentType }}</span>
        </div>
        <div class="bai-res-context__item">
          <span class="bai-res-context__label">Household Size</span>
          <span class="bai-res-context__value">{{ ctx.householdSize }}</span>
        </div>
        @if (ctx.knownNeeds.length > 0) {
          <div class="bai-res-context__item bai-res-context__item--full">
            <span class="bai-res-context__label">Known Needs</span>
            <span class="bai-res-context__value bai-res-context__needs">
              @for (need of ctx.knownNeeds; track need) {
                <span class="bai-res-context__need-chip">{{ need }}</span>
              }
            </span>
          </div>
        }
      </div>
    </section>

    <!-- ── 2. Employer Benefits ────────────────────────────────────────────── -->
    <section class="bai-res-card" aria-labelledby="employer-title">
      <h2 id="employer-title" class="bai-res-section-title">
        <i class="pi pi-building" aria-hidden="true"></i>
        Employer Benefits
        <span class="bai-res-section-count">{{ employerResources().length }}</span>
      </h2>
      @if (employerResources().length === 0) {
        <p class="bai-res-empty-inline">No employer benefits found for this client.</p>
      } @else {
        <div class="bai-res-list" role="list">
          @for (res of employerResources(); track res.resourceId) {
            <div class="bai-res-row" role="listitem">
              <ng-container *ngTemplateOutlet="resourceRow; context: { $implicit: res }" />
            </div>
          }
        </div>
      }
    </section>

    <!-- ── 3. Community Resources ──────────────────────────────────────────── -->
    <section class="bai-res-card" aria-labelledby="community-title">
      <h2 id="community-title" class="bai-res-section-title">
        <i class="pi pi-users" aria-hidden="true"></i>
        Local Community Resources
        <span class="bai-res-section-count">{{ communityResources().length }}</span>
      </h2>
      @if (communityResources().length === 0) {
        <p class="bai-res-empty-inline">No community resources found for this region.</p>
      } @else {
        <div class="bai-res-list" role="list">
          @for (res of communityResources(); track res.resourceId) {
            <div class="bai-res-row" role="listitem">
              <ng-container *ngTemplateOutlet="resourceRow; context: { $implicit: res }" />
            </div>
          }
        </div>
      }
    </section>

    <!-- ── 4. All Resources Explorer ──────────────────────────────────────── -->
    <section class="bai-res-card bai-res-explorer" aria-labelledby="explorer-title">
      <h2 id="explorer-title" class="bai-res-section-title">
        <i class="pi pi-search" aria-hidden="true"></i>
        All Resources Explorer
        <span class="bai-res-section-count">{{ filteredForExplorer().length }}</span>
      </h2>

      <!-- Filters row -->
      <div class="bai-res-filters" role="search" aria-label="Filter resources">
        <!-- Search -->
        <div class="bai-res-filters__search-wrap">
          <i class="pi pi-search bai-res-filters__search-icon" aria-hidden="true"></i>
          <input
            type="search"
            class="bai-res-filters__search"
            placeholder="Search name, category, tags…"
            [value]="f.search"
            (input)="setSearch($any($event.target).value)"
            aria-label="Search resources"
          />
        </div>

        <!-- Type -->
        <select
          class="bai-res-filters__select"
          [value]="f.type"
          (change)="setTypeFilter($any($event.target).value)"
          aria-label="Filter by type"
        >
          @for (opt of resourceTypes; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>

        <!-- Category -->
        <select
          class="bai-res-filters__select"
          [value]="f.category"
          (change)="setCategoryFilter($any($event.target).value)"
          aria-label="Filter by category"
        >
          @for (opt of resourceCategories; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>

        <!-- Eligibility -->
        <select
          class="bai-res-filters__select"
          [value]="f.eligibilityStatus"
          (change)="setEligibilityFilter($any($event.target).value)"
          aria-label="Filter by eligibility"
        >
          @for (opt of eligibilityStatuses; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>

        <!-- Sort -->
        <select
          class="bai-res-filters__select"
          [value]="f.sortKey"
          (change)="setSortKey($any($event.target).value)"
          aria-label="Sort resources"
        >
          @for (opt of sortOptions; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>

        @if (filterCount(f) > 0) {
          <button
            type="button"
            class="bai-res-filters__clear"
            (click)="clearFilters()"
            aria-label="Clear all filters"
          >
            <i class="pi pi-times" aria-hidden="true"></i> Clear
          </button>
        }
      </div>

      <!-- Explorer results -->
      @if (explorerPageItems().length === 0) {
        <div class="bai-res-empty-inline">
          @if (filterCount(f) > 0) {
            No resources match current filters.
            <button type="button" class="bai-res-link" (click)="clearFilters()">Clear filters</button>
          } @else {
            No resources available.
          }
        </div>
      } @else {
        <div class="bai-res-list" role="list">
          @for (res of explorerPageItems(); track res.resourceId) {
            <div class="bai-res-row" role="listitem">
              <ng-container *ngTemplateOutlet="resourceRow; context: { $implicit: res }" />
            </div>
          }
        </div>

        <!-- Pagination -->
        @if (explorerTotalPages() > 1) {
          <div class="bai-res-pagination" role="navigation" aria-label="Explorer pagination">
            <button
              type="button"
              class="bai-res-pagination__btn"
              [disabled]="explorerPage() === 0"
              (click)="setExplorerPage(explorerPage() - 1)"
              aria-label="Previous page"
            >
              <i class="pi pi-angle-left" aria-hidden="true"></i>
            </button>
            @for (p of pageRange(explorerTotalPages()); track p) {
              <button
                type="button"
                class="bai-res-pagination__btn"
                [class.bai-res-pagination__btn--active]="p === explorerPage()"
                (click)="setExplorerPage(p)"
                [attr.aria-current]="p === explorerPage() ? 'page' : null"
                [attr.aria-label]="'Page ' + (p + 1)"
              >{{ p + 1 }}</button>
            }
            <button
              type="button"
              class="bai-res-pagination__btn"
              [disabled]="explorerPage() === explorerTotalPages() - 1"
              (click)="setExplorerPage(explorerPage() + 1)"
              aria-label="Next page"
            >
              <i class="pi pi-angle-right" aria-hidden="true"></i>
            </button>
          </div>
        }
      }
    </section>
  }

</div>

<!-- ── Shared resource row template ──────────────────────────────────────── -->
<ng-template #resourceRow let-res>
  <div class="bai-res-row__header">
    <!-- Left: icon + name + type + category -->
    <div class="bai-res-row__identity">
      <i [class]="'bai-res-row__type-icon ' + typeIcon(res.type)" aria-hidden="true"></i>
      <div class="bai-res-row__name-block">
        <span class="bai-res-row__name">{{ res.name }}</span>
        <span class="bai-res-row__provider">{{ res.providerName }}</span>
      </div>
      <span class="bai-res-row__cat-chip">{{ categoryDisplayLabel(res.category) }}</span>
    </div>

    <!-- Center: eligibility + confidence + rank -->
    <div class="bai-res-row__meta">
      <p-tag
        [value]="res.eligibilityStatus"
        [severity]="eligibilitySeverity(res.eligibilityStatus)"
        [pTooltip]="res.whyShownText"
        tooltipPosition="top"
      />
      <span
        class="bai-res-row__confidence"
        [pTooltip]="'Confidence: ' + confidenceLabel(res.confidenceLevel)"
        tooltipPosition="top"
        [attr.aria-label]="'Confidence: ' + confidenceLabel(res.confidenceLevel)"
      >
        <i class="pi pi-shield" aria-hidden="true"></i>
        {{ confidenceLabel(res.confidenceLevel) }}
      </span>
      <span
        class="bai-res-row__rank"
        [class]="rankScoreClass(res.rankScore)"
        [pTooltip]="'Fit score: ' + res.rankScore + '/100'"
        tooltipPosition="top"
        [attr.aria-label]="'Fit score ' + res.rankScore + ' out of 100'"
      >{{ res.rankScore }}</span>
    </div>

    <!-- Right: CTAs + save -->
    <div class="bai-res-row__actions" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
      @if (res.contactPhone) {
        <button
          type="button"
          class="bai-res-row__cta"
          (click)="callResource(res)"
          [pTooltip]="res.contactPhone"
          tooltipPosition="top"
          [attr.aria-label]="'Call ' + res.name"
        >
          <i class="pi pi-phone" aria-hidden="true"></i>
        </button>
      }
      @if (res.contactEmail) {
        <button
          type="button"
          class="bai-res-row__cta"
          (click)="emailResource(res)"
          [pTooltip]="res.contactEmail"
          tooltipPosition="top"
          [attr.aria-label]="'Email ' + res.name"
        >
          <i class="pi pi-envelope" aria-hidden="true"></i>
        </button>
      }
      @if (res.websiteUrl) {
        <button
          type="button"
          class="bai-res-row__cta"
          (click)="openWebsite(res)"
          pTooltip="Open website"
          tooltipPosition="top"
          [attr.aria-label]="'Open website for ' + res.name"
        >
          <i class="pi pi-external-link" aria-hidden="true"></i>
        </button>
      }
      <button
        type="button"
        class="bai-res-row__cta"
        [class.bai-res-row__cta--saved]="res.savedByFA"
        (click)="toggleSaved(res)"
        [pTooltip]="res.savedByFA ? 'Unsave' : 'Save'"
        tooltipPosition="top"
        [attr.aria-label]="(res.savedByFA ? 'Unsave' : 'Save') + ' ' + res.name"
        [attr.aria-pressed]="res.savedByFA"
      >
        <i [class]="res.savedByFA ? 'pi pi-bookmark-fill' : 'pi pi-bookmark'" aria-hidden="true"></i>
      </button>
      <details class="bai-res-row__kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
        <summary class="bai-res-row__kebab-btn" aria-label="Resource options">
          <i class="pi pi-bars" aria-hidden="true"></i>
        </summary>
        <button
          type="button"
          class="bai-res-row__kebab-action"
          (click)="addResourceToCopilot(res)"
        >
          Add to Copilot
        </button>
      </details>
      <button
        type="button"
        class="bai-res-row__why-btn"
        (click)="toggleWhy(res.resourceId)"
        [attr.aria-expanded]="isWhyExpanded(res.resourceId)"
        [attr.aria-label]="'Toggle eligibility details for ' + res.name"
      >
        <i [class]="isWhyExpanded(res.resourceId) ? 'pi pi-angle-up' : 'pi pi-angle-down'" aria-hidden="true"></i>
        Why shown
      </button>
    </div>
  </div>

  <!-- Expandable eligibility detail -->
  @if (isWhyExpanded(res.resourceId)) {
    <div class="bai-res-row__detail" role="region" [attr.aria-label]="'Eligibility details for ' + res.name">
      <p class="bai-res-row__why-text">{{ res.whyShownText }}</p>

      @if (res.whyNotShownText) {
        <p class="bai-res-row__why-not">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          {{ res.whyNotShownText }}
        </p>
      }

      <div class="bai-res-row__criteria">
        @if (res.criteriaMet.length > 0) {
          <div class="bai-res-row__criteria-group">
            <span class="bai-res-row__criteria-label bai-res-row__criteria-label--met">
              <i class="pi pi-check-circle" aria-hidden="true"></i> Met
            </span>
            @for (c of res.criteriaMet; track c) {
              <span class="bai-res-row__criterion bai-res-row__criterion--met">{{ c }}</span>
            }
          </div>
        }
        @if (res.criteriaMissing.length > 0) {
          <div class="bai-res-row__criteria-group">
            <span class="bai-res-row__criteria-label bai-res-row__criteria-label--missing">
              <i class="pi pi-times-circle" aria-hidden="true"></i> Missing
            </span>
            @for (c of res.criteriaMissing; track c) {
              <span class="bai-res-row__criterion bai-res-row__criterion--missing">{{ c }}</span>
            }
          </div>
        }
        @if (res.criteriaUnknown.length > 0) {
          <div class="bai-res-row__criteria-group">
            <span class="bai-res-row__criteria-label bai-res-row__criteria-label--unknown">
              <i class="pi pi-minus-circle" aria-hidden="true"></i> Unknown
            </span>
            @for (c of res.criteriaUnknown; track c) {
              <span class="bai-res-row__criterion bai-res-row__criterion--unknown">{{ c }}</span>
            }
          </div>
        }
      </div>

      @if (res.contactedAt) {
        <p class="bai-res-row__contacted">
          <i class="pi pi-check" aria-hidden="true"></i>
          Contacted via {{ res.contactMethod }} on {{ formatDate(res.contactedAt) }}
        </p>
      }

      <p class="bai-res-row__verified">
        Last verified: {{ formatDate(res.lastVerifiedAt) }} · Source: {{ res.sourceSystem }}
      </p>
    </div>
  }
</ng-template>
